import { cn } from '@/lib/utils';
import { useMemo, useState, useCallback, useContext } from 'react';

import { 
  ChainlitContext,
  IStep,
  useAuth,
  useChatData,
  useChatInteract
} from '@chainlit/react-client';

import { ChevronUp, ChevronDown } from 'lucide-react';
import { useRecoilValue } from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import { persistentCommandState } from '@/state/chat';
import { useUISettings } from '@/hooks/useUISettings';

interface Props {
  className?: string;
}

interface ExpandableStarterProps {
  tile: {name: string, title: string, questions: string[], icon: string, color: string};
  isExpanded: boolean;
  onToggle: () => void;
}

function ExpandableStarter({ tile, isExpanded, onToggle }: ExpandableStarterProps) {
  const apiClient = useContext(ChainlitContext);
  const selectedCommand = useRecoilValue(persistentCommandState);
  const { sendMessage } = useChatInteract();
  const { loading, connected } = useChatData();
  const { user } = useAuth();

  const disabled = loading || !connected;

  const onQuestionClick = useCallback(async (question: string) => {
    const message: IStep = {
      threadId: '',
      id: uuidv4(),
      command: selectedCommand?.id,
      name: user?.identifier || 'User',
      type: 'user_message',
      output: question,
      createdAt: new Date().toISOString(),
      metadata: { location: window.location.href }
    };
    sendMessage(message, []);
  }, [user, selectedCommand, sendMessage]);

  return (
    <div className="border border-gray-200 rounded-[2rem] bg-white overflow-hidden">
      {/* Header with chevron left, title closer to chevron, logo right */}
      <div 
        className="flex items-center px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-center w-6 h-6 mr-3">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-600 transition-transform duration-200" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-600 transition-transform duration-200" />
          )}
        </div>
        <h3 className="text-lg font-medium text-gray-900 flex-1">{tile.title}</h3>
        <div className="flex items-center justify-end">
          <img
            className="h-7 w-7 flex-shrink-0 object-cover rounded-full"
            src={
              (() => {
                const finalSrc = tile.icon && tile.icon.trim() !== ''
                  ? (tile.icon.startsWith('/public') ? apiClient.buildEndpoint(tile.icon) : tile.icon)
                  : apiClient.buildEndpoint('/public/favicon.png');
                console.log('ExpandableStarter icon logic:', {
                  originalIcon: tile.icon,
                  hasIcon: !!(tile.icon && tile.icon.trim() !== ''),
                  startsWithPublic: tile.icon?.startsWith('/public'),
                  usingBuildEndpoint: tile.icon?.startsWith('/public') || !tile.icon || tile.icon.trim() === '',
                  finalSrc: finalSrc
                });
                return finalSrc;
              })()
            }
            alt={tile.title}
            onError={(e) => {
              console.log('ExpandableStarter image failed to load, using favicon fallback');
              e.currentTarget.src = apiClient.buildEndpoint('/public/favicon.png');
            }}
          />
        </div>
      </div>

      {/* Expandable content with slide animation */}
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="border-t border-gray-200">
          <div className="px-4 py-2">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Common Questions:</h4>
            <div className="space-y-2">
              {/* Common questions */}
              {tile.questions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => onQuestionClick(question)}
                  disabled={disabled}
                  className="w-full text-left p-2 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface SquareStarterProps {
  tile: {name: string, title: string, questions: string[], icon: string, color: string};
  isExpanded: boolean;
  onToggle: () => void;
}

function SquareStarter({ tile, isExpanded, onToggle }: SquareStarterProps) {
  const apiClient = useContext(ChainlitContext);
  const selectedCommand = useRecoilValue(persistentCommandState);
  const { sendMessage } = useChatInteract();
  const { loading, connected } = useChatData();
  const { user } = useAuth();

  const disabled = loading || !connected;

  const onQuestionClick = useCallback(async (question: string) => {
    const message: IStep = {
      threadId: '',
      id: uuidv4(),
      command: selectedCommand?.id,
      name: user?.identifier || 'User',
      type: 'user_message',
      output: question,
      createdAt: new Date().toISOString(),
      metadata: { location: window.location.href }
    };
    sendMessage(message, []);
  }, [user, selectedCommand, sendMessage]);

  return (
    <div className="w-[130px] flex flex-col items-center">
      {/* Square tile */}
      <div
        className="h-[130px] w-[130px] rounded-2xl border border-gray-200 bg-white p-3 cursor-pointer hover:scale-[1.02] hover:shadow-md active:scale-[0.98] transition-all duration-200 flex flex-col items-center justify-between"
        onClick={onToggle}
      >
        <div></div>
        <p className="text-sm text-center text-gray-900 font-medium leading-tight">
          {tile.title}
        </p>
        
        {/* Chevron positioned at bottom, moved up slightly */}
        <div className="flex items-center justify-center -mt-2">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-600 transition-transform duration-200" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-600 transition-transform duration-200" />
          )}
        </div>
      </div>

      {/* Expandable content with slide animation - centered below tile */}
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="mt-2 w-[300px] border border-gray-200 rounded-2xl bg-white p-3">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Common Questions:</h4>
          <div className="space-y-2">
            {/* Common questions */}
            {tile.questions.map((question, index) => (
              <button
                key={index}
                onClick={() => onQuestionClick(question)}
                disabled={disabled}
                className="w-full text-left p-3 bg-gray-100 hover:bg-gray-200 text-gray-800 text-sm rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Starters({ className }: Props) {
  const { settings } = useUISettings();
  const [expandedStarter, setExpandedStarter] = useState<number | null>(null);

  // Use tiles from UI settings as starters
  const tiles = settings?.tiles;

  if (!tiles?.length) return null;

  const toggleStarter = (index: number) => {
    setExpandedStarter(prev => prev === index ? null : index);
  };

  return (
    <div
      id="starters"
      className={cn(
        'w-full px-4 mx-auto',
        'max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-5xl',
        className
      )}
    >
      {/* Mobile: Expandable sections */}
      <div className="sm:hidden space-y-4">
        {tiles?.map((tile, i) => (
          <ExpandableStarter
            key={i}
            tile={tile}
            isExpanded={expandedStarter === i}
            onToggle={() => toggleStarter(i)}
          />
        ))}
      </div>

      {/* Desktop: Square tiles */}
      <div className="hidden sm:flex sm:flex-row sm:flex-wrap sm:gap-4 sm:justify-center sm:px-6">
        {tiles?.map((tile, i) => (
          <SquareStarter
            key={i}
            tile={tile}
            isExpanded={expandedStarter === i}
            onToggle={() => toggleStarter(i)}
          />
        ))}
      </div>
    </div>
  );
}
